import type { Party, PortalStatement } from '@erp/shared';
import Decimal from 'decimal.js';
import { Banknote, FileText, HandCoins, Printer } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Card, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { useBranchScope } from '../../lib/branches.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { normalizeAmountTyping, safeDecimal } from '../../lib/numerals.js';

/**
 * Staff account view — the balance panel on a party's profile, mirroring the
 * portal statement. For a customer (`side='customer'`) this is AR: what they owe
 * us, over issued invoices + inbound payments. For a supplier (`side='vendor'`)
 * it is AP: what WE owe them, over approved bills + outbound payments. Shows the
 * balance per currency, credit-limit usage (customers), aging, open documents and
 * payments, plus one-click credit invoice/bill and record-a-payment.
 */
export function CustomerStatementCard({ party, onChanged, side = 'customer', onRecordPayment }: {
  party: Party;
  onChanged: () => void;
  side?: 'customer' | 'vendor';
  /**
   * Take over the "Record payment" button. The supplier page passes this so the
   * payment runs through its own dialog (which allocates across several open
   * bills and posts in one call); without it the card keeps its built-in
   * single-document dialog. Passing it also means the caller owns the
   * permission check for whatever that dialog does.
   */
  onRecordPayment?: () => void;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc, i18n } = useTranslation();
  const isVendor = side === 'vendor';
  const canInvoice = usePermission('invoices.write');
  const canBill = usePermission('bills.write');
  const canCreateDoc = isVendor ? canBill : canInvoice;
  // A customer's money outside the register needs payments.receive — counter
  // staff take it in their open shift (the register's debt payment).
  const canPay = usePermission(isVendor ? 'payments.write' : 'payments.receive');
  const docBase = isVendor ? '/purchasing/bills' : '/sales/invoices';
  const [st, setSt] = useState<PortalStatement | null>(null);
  const [showPay, setShowPay] = useState(false);
  /** The dialog opened from «دفع للزبون»: money going TO the customer. */
  const [payOut, setPayOut] = useState(false);

  const reload = async (): Promise<void> => {
    try { setSt((await api().get<PortalStatement>(`/parties/${party.id}/statement${isVendor ? '?side=vendor' : ''}`)).data); } catch { /* leave skeleton */ }
  };
  useEffect(() => { void reload(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [party.id]);

  const money = (a: string, c: string): string => formatMoney(a, { locale: i18n.language, currency: c });
  const openInvoices = useMemo(() => (st?.invoices ?? []).filter((i) => new Decimal(i.balance).gt('0.0001')), [st]);

  if (!st) {
    return <Card><Skeleton className="h-5 w-40" /><Skeleton className="mt-3 h-24 w-full" /></Card>;
  }

  const hasDebt = st.summary.some((s) => new Decimal(s.balance).gt('0.0001'));
  // Preselect the currency we owe/are owed the most in. Money strings are
  // compared as Decimals — never coerced to float (ADR-0005).
  const largestBalanceCurrency = [...st.summary]
    .sort((a, b) => new Decimal(b.balance).comparedTo(a.balance))[0]?.currencyCode;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-fg-subtle">{t('account.title', { defaultValue: 'Account / Statement' })}</h2>
        <div className="flex flex-wrap gap-2">
          {canCreateDoc && (
            <Link to={`${docBase}/new?party=${party.id}`}>
              <Button variant="secondary" size="sm"><FileText className="me-2 h-4 w-4" />{isVendor ? t('account.newBill', { defaultValue: 'New bill (credit)' }) : t('account.sellOnCredit', { defaultValue: 'New invoice (credit)' })}</Button>
            </Link>
          )}
          {!isVendor && (
            <Link to={`/sales/parties/${party.id}/statement/print`}>
              <Button variant="secondary" size="sm"><Printer className="me-2 h-4 w-4" />{t('account.printStatement')}</Button>
            </Link>
          )}
          {/* The other way round (owner, 2026-11-01): money the shop hands TO the
              customer — what typing a minus in «تسجيل دفعة» does, as its own button. */}
          {!isVendor && !onRecordPayment && canPay && (
            <Button variant="secondary" size="sm" onClick={() => { setPayOut(true); setShowPay(true); }}>
              <Banknote className="me-2 h-4 w-4" />{t('account.payOut', { defaultValue: 'Pay the customer' })}
            </Button>
          )}
          {(onRecordPayment ?? canPay) && (
            <Button size="sm" onClick={onRecordPayment ?? (() => { setPayOut(false); setShowPay(true); })}><HandCoins className="me-2 h-4 w-4" />{t('account.recordPayment', { defaultValue: 'Record payment' })}</Button>
          )}
        </div>
      </div>

      {/* Balance / debt per currency */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {st.summary.length === 0 ? (
          <Card className="sm:col-span-2 lg:col-span-3 text-sm text-fg-muted">{isVendor ? t('account.noActivityVendor', { defaultValue: 'No bills yet for this supplier.' }) : t('account.noActivity', { defaultValue: 'No invoices yet for this customer.' })}</Card>
        ) : st.summary.map((s) => {
          const owes = Number(s.balance) > 0.0001;
          return (
            <Card key={s.currencyCode} className={owes ? 'border-warning/40' : undefined}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-fg-muted">{owes ? (isVendor ? t('account.weOwe', { defaultValue: 'We owe' }) : t('account.owes', { defaultValue: 'Outstanding' })) : t('account.settled', { defaultValue: 'Balance' })}</span>
                <span className="font-mono text-xs text-fg-muted">{s.currencyCode}</span>
              </div>
              <div className={`mt-1 font-mono text-2xl font-bold ${owes ? 'text-warning' : 'text-fg'}`}>{money(s.balance, s.currencyCode)}</div>
              <div className="mt-1 text-xs text-fg-muted">
                {isVendor ? t('account.billed', { defaultValue: 'Billed' }) : t('account.invoiced', { defaultValue: 'Invoiced' })} {money(s.invoiced, s.currencyCode)} · {t('account.paid', { defaultValue: 'Paid' })} {money(s.paid, s.currencyCode)}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Credit limit usage — a customer (AR) concept; only when a credit currency
          is set (matches PartyDetailPage); guessing it from the first statement
          row could gauge the limit against a balance in the wrong currency. */}
      {!isVendor && party.creditLimit && Number(party.creditLimit) > 0 && party.creditCurrencyCode && (() => {
        const ccy = party.creditCurrencyCode;
        const used = Number(st.summary.find((s) => s.currencyCode === ccy)?.balance ?? '0');
        const limit = Number(party.creditLimit);
        const pct = Math.min(100, Math.round((used / limit) * 100));
        const over = used > limit;
        return (
          <Card>
            <div className="flex items-center justify-between text-sm">
              <span className="text-fg-muted">{t('account.creditUsed', { defaultValue: 'Credit used' })}</span>
              <span className={`font-mono ${over ? 'text-danger' : 'text-fg'}`}>{money(String(used), ccy)} / {money(party.creditLimit, ccy)}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg-subtle">
              <div className={`h-full rounded-full ${over ? 'bg-danger' : pct > 80 ? 'bg-warning' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
            </div>
            {over && <p className="mt-1 text-xs text-danger">{t('account.overLimit', { defaultValue: 'Over credit limit.' })}</p>}
          </Card>
        );
      })()}

      {/* Aging */}
      {st.aging.length > 0 && hasDebt && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-bg-muted text-xs uppercase tracking-wide text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-start">{tc('columns.currency', { defaultValue: 'Currency' })}</th>
                <th className="px-3 py-2 text-end">{t('account.aging.current', { defaultValue: 'Current' })}</th>
                <th className="px-3 py-2 text-end">1–30</th>
                <th className="px-3 py-2 text-end">31–60</th>
                <th className="px-3 py-2 text-end">61–90</th>
                <th className="px-3 py-2 text-end">90+</th>
                <th className="px-3 py-2 text-end font-semibold">{t('account.aging.total', { defaultValue: 'Total' })}</th>
              </tr>
            </thead>
            <tbody>
              {st.aging.map((a) => (
                <tr key={a.currencyCode} className="border-t border-border font-mono">
                  <td className="px-3 py-2 text-start">{a.currencyCode}</td>
                  <td className="px-3 py-2 text-end">{money(a.current, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_1_30, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_31_60, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_61_90, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end text-danger">{money(a.days_90_plus, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end font-semibold">{money(a.total, a.currencyCode)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Open invoices + payments */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-0">
          <h3 className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {isVendor ? t('account.openBills', { defaultValue: 'Open bills' }) : t('account.openInvoices', { defaultValue: 'Open invoices' })} {openInvoices.length > 0 && <span className="text-fg-subtle">({openInvoices.length})</span>}
          </h3>
          {openInvoices.length === 0 ? (
            <p className="px-4 py-4 text-sm text-fg-muted">{t('account.noOpen', { defaultValue: 'Nothing outstanding 🎉' })}</p>
          ) : (
            <ul className="divide-y divide-border">
              {openInvoices.slice(0, 8).map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-2 px-4 py-2">
                  <Link to={`${docBase}/${inv.id}`} className="min-w-0">
                    <div className="truncate font-mono text-sm text-brand">{inv.docNumber}</div>
                    <div className="text-xs text-fg-muted">{formatDate(inv.docDate, { locale: i18n.language })}{inv.dueDate ? ` · ${t('account.due', { defaultValue: 'due' })} ${formatDate(inv.dueDate, { locale: i18n.language })}` : ''}</div>
                  </Link>
                  <span className="font-mono text-sm font-medium text-warning">{money(inv.balance, inv.currencyCode)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-0">
          <h3 className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('account.recentPayments', { defaultValue: 'Recent payments' })}</h3>
          {st.payments.length === 0 ? (
            <p className="px-4 py-4 text-sm text-fg-muted">{t('account.noPayments', { defaultValue: 'No payments yet.' })}</p>
          ) : (
            <ul className="divide-y divide-border">
              {st.payments.slice(0, 8).map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 px-4 py-2">
                  <div className="min-w-0">
                    <div className="truncate font-mono text-sm text-fg">{p.docNumber}</div>
                    <div className="text-xs text-fg-muted">{formatDate(p.paymentDate, { locale: i18n.language })}{p.method ? ` · ${p.method}` : ''}</div>
                  </div>
                  <span className="font-mono text-sm text-success">{money(p.amount, p.currencyCode)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {showPay && (
        <RecordPaymentDialog
          party={party}
          side={side}
          openInvoices={openInvoices}
          summary={st.summary}
          defaultCurrency={largestBalanceCurrency}
          payOut={payOut}
          onClose={() => setShowPay(false)}
          onDone={async () => { await reload(); onChanged(); }}
        />
      )}
    </div>
  );
}

type ApplyMode = 'auto' | 'manual' | 'account';
const METHODS = ['cash', 'bank_transfer', 'fib', 'cheque', 'other'] as const;

/**
 * Record a payment — and see exactly where it goes.
 *
 * It used to put the WHOLE amount on the one invoice picked (the oldest by
 * default): a 1,000 USD receipt landed on a 32.10 invoice and the customer's
 * other invoices never came down. Now the payment is spread over the open
 * invoices oldest-first — never more than one still owes (the API refuses
 * that too) — or across invoices the operator picks, and whatever is left
 * stays on account. The customer's balance now and after this payment, and
 * every invoice with what it owes, what this pays and what is left, are on
 * screen before anything is saved.
 */
function RecordPaymentDialog({ party, side = 'customer', openInvoices, summary, defaultCurrency, payOut = false, onClose, onDone }: {
  party: Party;
  /**
   * «دفع للزبون»: the amount is money handed TO the customer — typed as a plain
   * number and booked as the minus payment (on account, raises what they owe).
   */
  payOut?: boolean;
  /** 'customer' → inbound receipt against invoices; 'vendor' → outbound payment against bills. */
  side?: 'customer' | 'vendor';
  openInvoices: PortalStatement['invoices'];
  summary: PortalStatement['summary'];
  defaultCurrency?: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('parties');
  const { t: tc, i18n } = useTranslation();
  const isVendor = side === 'vendor';
  const baseCcy = useAuthStore((st) => st.tenant?.baseCurrencyCode ?? 'USD');
  const [currency, setCurrency] = useState((defaultCurrency ?? baseCcy).toUpperCase());
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [method, setMethod] = useState<string>('cash');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  // «على الحساب» first (owner, 2026-11-01); the oldest-first split and picking
  // invoices are one click away. The balance is the same either way — money on
  // account comes off it everywhere (payments.enrich unappliedPayments).
  const [mode, setMode] = useState<ApplyMode>('account');
  const [manual, setManual] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const money = (a: Decimal | string, c = currency): string => formatMoney(new Decimal(a).toFixed(4), { locale: i18n.language, currency: c });

  /**
   * The BRANCH the money is taken at. A branch's debt is its own invoices and its
   * own payments, so a receipt left without one (what the owner's always were)
   * never brought any branch's balance down. Picked here whenever there is more
   * than one branch to choose from; someone confined to a single branch gets it
   * stamped by the server and sees no box.
   */
  const { branches, loaded: branchesLoaded } = useBranchScope();
  const [branch, setBranch] = useState('');
  const [branchPicked, setBranchPicked] = useState(false);
  const askBranch = branches.length > 1;
  const byDate = (a: PortalStatement['invoices'][number], b: PortalStatement['invoices'][number]): number =>
    a.docDate.localeCompare(b.docDate) || a.docNumber.localeCompare(b.docNumber);
  // Until someone chooses: the branch of the oldest debt in this currency (the one
  // the money pays first), else the customer's home branch, else the only branch.
  useEffect(() => {
    if (!branchesLoaded || branchPicked) return;
    const usable = (id?: string | null): id is string => !!id && branches.some((b) => b.id === id);
    const oldest = openInvoices.filter((inv) => inv.currencyCode === currency).slice().sort(byDate).find((inv) => usable(inv.warehouseId));
    setBranch(oldest?.warehouseId ?? (usable(party.warehouseId) ? party.warehouseId : (branches.length === 1 ? branches[0]!.id : '')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchesLoaded, branchPicked, branches, openInvoices, currency, party.warehouseId]);

  // Open invoices in the payment's currency AT the chosen branch (and unbranched
  // ones, which any branch may settle), OLDEST first — the order money is applied in.
  const allocatable = useMemo(
    () => openInvoices
      .filter((inv) => inv.currencyCode === currency && (!askBranch || !branch || !inv.warehouseId || inv.warehouseId === branch))
      .slice()
      .sort(byDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openInvoices, currency, askBranch, branch],
  );
  useEffect(() => { setManual({}); }, [currency, branch]);
  // Where this currency's debt is, when it is not at the chosen branch.
  const otherBranches = useMemo(() => {
    if (!askBranch || allocatable.length > 0) return [];
    const ids = new Set(openInvoices.filter((inv) => inv.currencyCode === currency && inv.warehouseId && inv.warehouseId !== branch).map((inv) => inv.warehouseId!));
    return branches.filter((b) => ids.has(b.id)).map((b) => b.name);
  }, [askBranch, allocatable, openInvoices, currency, branch, branches]);

  // Paying the customer: whatever is typed is money going out — a minus.
  const entered = payOut ? safeDecimal(amount).abs().negated() : safeDecimal(amount);
  // A negative amount is money paid BACK (to the customer, or by the supplier):
  // it goes on account and raises the balance — it settles no document.
  const refund = payOut || entered.lt(0);
  const paid = Decimal.max(entered, 0);
  // What this payment puts on each invoice.
  const plan = useMemo(() => {
    const out = new Map<string, Decimal>();
    if (mode === 'account' || refund) return out;
    if (mode === 'auto') {
      let left = paid;
      for (const inv of allocatable) {
        if (left.lte(0)) break;
        const take = Decimal.min(new Decimal(inv.balance), left);
        out.set(inv.id, take);
        left = left.minus(take);
      }
      return out;
    }
    for (const inv of allocatable) {
      const raw = manual[inv.id];
      if (!raw) continue;
      try {
        const v = Decimal.min(Decimal.max(safeDecimal(raw), 0), new Decimal(inv.balance));
        if (v.gt(0)) out.set(inv.id, v);
      } catch { /* not a number yet */ }
    }
    return out;
  }, [mode, refund, paid, allocatable, manual]);
  const applied = [...plan.values()].reduce((a, v) => a.plus(v), new Decimal(0));
  const onAccount = refund ? entered : Decimal.max(paid.minus(applied), 0);
  const overApplied = applied.gt(paid);
  const balanceNow = new Decimal(summary.find((x) => x.currencyCode === currency)?.balance ?? '0');
  const balanceAfter = balanceNow.minus(refund ? entered : paid);

  const startManual = (): void => {
    // Start from the automatic split, so choosing is an edit, not a blank form.
    setManual(Object.fromEntries([...plan.entries()].map(([id, v]) => [id, v.toString()])));
    setMode('manual');
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (overApplied) { setError(t('account.overApplied')); return; }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        paymentDate: date,
        direction: isVendor ? 'outbound' : 'inbound',
        partyId: party.id,
        currencyCode: currency,
        ...(branch ? { warehouseId: branch } : {}),
        amount: (refund ? entered : paid).toFixed(4),
        method: method || undefined,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      const allocations = [...plan.entries()].filter(([, v]) => v.gt(0))
        .map(([targetId, v]) => ({ targetType: isVendor ? 'bill' : 'invoice', targetId, amount: v.toFixed(4) }));
      if (allocations.length) body.allocations = allocations;
      const created = await api().post<{ id: string }>('/payments', body);
      // Post it (DR cash / CR AR) — best-effort; the payment is recorded either way.
      try { await api().post(`/payments/${created.data.id}/post`, {}); } catch { /* stays draft for the office to post */ }
      toast.success(t('account.paymentRecorded', { defaultValue: 'Payment recorded' }));
      onClose();
      await onDone();
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { code?: string; cause?: { code?: string } } } }).response;
      const code = res?.data?.cause?.code ?? res?.data?.code;
      setError(
        code === 'allocation_currency_mismatch' ? t('account.allocationCurrencyMismatch', { defaultValue: 'The payment currency must match the invoice it settles.' })
          : code === 'allocation_exceeds_open' ? t('account.exceedsOpen')
            : code === 'warehouse_not_allowed_for_user' ? t('account.notYourBranch')
              : tc('errors:server'),
      );
    } finally { setBusy(false); }
  };

  const modeBtn = (m: ApplyMode, label: string, onClick?: () => void): JSX.Element => (
    <button
      type="button"
      onClick={onClick ?? (() => setMode(m))}
      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${mode === m ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}
      aria-pressed={mode === m}
    >
      {label}
    </button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {payOut ? t('account.payOut', { defaultValue: 'Pay the customer' }) : t('account.recordPayment', { defaultValue: 'Record payment' })} — {party.displayName}
            </DialogTitle>
          </DialogHeader>

          {/* Where the customer stands, in every currency they have a balance in. */}
          {summary.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {summary.map((x) => (
                <button
                  key={x.currencyCode}
                  type="button"
                  onClick={() => setCurrency(x.currencyCode)}
                  className={`rounded-lg border px-3 py-1.5 text-start text-xs transition ${x.currencyCode === currency ? 'border-brand bg-brand-subtle' : 'border-border hover:bg-bg-subtle'}`}
                >
                  <div className="text-fg-muted">{t('account.balanceNow')} · {x.currencyCode}</div>
                  <div className={`font-mono text-sm font-bold ${new Decimal(x.balance).gt(0) ? 'text-warning' : 'text-success'}`} dir="ltr">
                    {money(x.balance, x.currencyCode)}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-3">
            <FormField id="rp-amount" label={payOut ? t('account.payOutAmount', { defaultValue: 'Amount paid to the customer' }) : t('account.amount', { defaultValue: 'Amount' })} required>
              <Input id="rp-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(normalizeAmountTyping(e.target.value))} autoFocus required dir="ltr" />
            </FormField>
            <FormField id="rp-ccy" label={tc('columns.currency', { defaultValue: 'Currency' })}>
              <CurrencySelect id="rp-ccy" value={currency} onChange={setCurrency} />
            </FormField>
            <FormField id="rp-date" label={t('account.date')}>
              <Input id="rp-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </FormField>
            {askBranch && (
              <FormField id="rp-branch" label={t('branch.label')} required>
                <select
                  id="rp-branch"
                  className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg"
                  value={branch}
                  onChange={(e) => { setBranch(e.target.value); setBranchPicked(true); }}
                  required
                >
                  <option value="" disabled>{t('branch.choose')}</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </FormField>
            )}
            <FormField id="rp-method" label={t('account.method', { defaultValue: 'Method' })}>
              <select
                id="rp-method"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                {METHODS.map((m) => <option key={m} value={m}>{t(`account.methods.${m}`)}</option>)}
              </select>
            </FormField>
            <FormField id="rp-ref" label={t('account.reference')}>
              <Input id="rp-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={t('account.referenceHint')} />
            </FormField>
            <FormField id="rp-notes" label={t('account.notes')}>
              <Input id="rp-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </FormField>
          </div>

          {/* Where the money goes — money paid out settles nothing, so no choice. */}
          {!refund && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('account.applyMode')}</span>
              <div className="inline-flex gap-1 rounded-lg border border-border p-0.5">
                {modeBtn('auto', t('account.modeAuto'))}
                {modeBtn('manual', t('account.modeManual'), startManual)}
                {modeBtn('account', t('account.modeAccount'))}
              </div>
            </div>
          )}

          {mode !== 'account' && !refund && (
            allocatable.length === 0 ? (
              <p className="mt-2 rounded-md bg-bg-subtle px-3 py-2 text-sm text-fg-muted">
                {otherBranches.length > 0
                  ? t('account.noOpenAtBranch', { ccy: currency, branches: otherBranches.join('، ') })
                  : t('account.noOpenInCurrency', { ccy: currency })}
              </p>
            ) : (
              <div className="mt-2 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-bg-muted text-xs text-fg-subtle">
                    <tr>
                      <th className="px-3 py-2 text-start">{t('account.colInvoice')}</th>
                      <th className="px-3 py-2 text-start">{t('account.colDate')}</th>
                      <th className="px-3 py-2 text-end">{t('account.colTotal')}</th>
                      <th className="px-3 py-2 text-end">{t('account.colOpen')}</th>
                      <th className="px-3 py-2 text-end">{t('account.colApply')}</th>
                      <th className="px-3 py-2 text-end">{t('account.colAfter')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocatable.map((inv) => {
                      const take = plan.get(inv.id) ?? new Decimal(0);
                      const after = new Decimal(inv.balance).minus(take);
                      return (
                        <tr key={inv.id} className={`border-t border-border ${take.gt(0) ? 'bg-bg-subtle' : ''}`}>
                          <td className="px-3 py-1.5 font-mono text-xs">{inv.docNumber}</td>
                          <td className="px-3 py-1.5 text-xs text-fg-muted">{formatDate(inv.docDate, { locale: i18n.language })}</td>
                          <td className="px-3 py-1.5 text-end font-mono text-xs" dir="ltr">{money(inv.total)}</td>
                          <td className="px-3 py-1.5 text-end font-mono text-xs text-warning" dir="ltr">{money(inv.balance)}</td>
                          <td className="px-3 py-1.5 text-end">
                            {mode === 'manual' ? (
                              <Input
                                aria-label={`${t('account.colApply')} ${inv.docNumber}`}
                                inputMode="decimal"
                                dir="ltr"
                                className="ms-auto h-8 w-28 text-end font-mono text-xs"
                                value={manual[inv.id] ?? ''}
                                placeholder="0"
                                onChange={(e) => setManual((m) => ({ ...m, [inv.id]: normalizeAmountTyping(e.target.value) }))}
                              />
                            ) : (
                              <span className={`font-mono text-xs ${take.gt(0) ? 'font-semibold text-success' : 'text-fg-subtle'}`} dir="ltr">{take.gt(0) ? money(take) : '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-end font-mono text-xs" dir="ltr">{after.lte(0) ? '✓' : money(after)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* The payment in three numbers, and the balance it leaves. */}
          <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-border bg-bg-subtle p-3 text-sm sm:grid-cols-4">
            <div>
              <div className="text-xs text-fg-muted">{t('account.amount', { defaultValue: 'Amount' })}</div>
              <div className={`font-mono font-semibold ${refund ? 'text-danger' : ''}`} dir="ltr">{money(refund ? entered : paid)}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted">{t('account.applied')}</div>
              <div className={`font-mono font-semibold ${overApplied ? 'text-danger' : ''}`} dir="ltr">{money(applied)}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted">{t('account.onAccount')}</div>
              <div className="font-mono font-semibold" dir="ltr">{money(onAccount)}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted">{t('account.balanceAfter')}</div>
              <div className={`font-mono font-bold ${balanceAfter.gt(0) ? 'text-warning' : 'text-success'}`} dir="ltr">
                {money(balanceAfter.abs())}{balanceAfter.lt(0) ? ` (${t('account.inCredit')})` : ''}
              </div>
            </div>
          </div>
          {refund && (
            <p className="mt-2 rounded-md bg-bg-subtle px-3 py-2 text-sm text-fg-muted">
              {t(isVendor ? 'account.refundHintVendor' : 'account.refundHint')}
            </p>
          )}
          {overApplied && <p className="mt-2 text-sm text-danger" role="alert">{t('account.overApplied')}</p>}
          {error && !overApplied && <p className="mt-2 text-sm text-danger" role="alert">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
            <Button type="submit" variant={payOut ? 'danger' : 'primary'} loading={busy} disabled={entered.isZero() || !/^[A-Z]{3}$/.test(currency) || overApplied || (askBranch && !branch)}>
              {payOut ? t('account.payOutSubmit', { defaultValue: 'Pay the customer' }) : t('account.recordPayment', { defaultValue: 'Record payment' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
